import React, { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import SessionExpiredPopup from 'components/SessionExpiredPopup';
import ThirdPartyAuthButtons from 'components/ThirdPartyAuthButtons';
import { EMAIL_MAX_LENGTH } from 'lib/constants';
import { redirect } from 'lib/navigation';
import { getLocationQuery, stringifyLocationQuery } from 'lib/url';
import { getLocalStorageItem, setLocalStorageItem } from 'lib/webStorage';
import eventTracking from 'services/analytics/eventTracking';
import { t } from 'ttag';

import { RegistrationProjectsSource } from '../registration/types';
import { AuthCheckEmailFormFields } from './types';

import Alert from '../ui/Alert/Alert';
import BackButton from '../ui/BackButton/BackButton';
import HorizontalLineSeparator from '../ui/HorizontalLineSeparator/HorizontalLineSeparator';
import Button from '../ui/button/button';
import OutlinedInput from '../ui/input/OutlinedInput/OutlinedInput';

import auth from '../../services/auth';
import FlexBox from '../FlexBox/FlexBox';
import { LS_KEY_AUTH_INITIAL_FORM } from '../auth/constants';
import PageTitle from '../pageTitle/pageTitle';
import { VALID_PROJECT_SOURCES } from '../registration/constants';
import { LANDINGS_REDIRECT_LINKS_MAP } from '../registration/hooks/useRegistrationRedirectLink';
import { useRegistrationSource } from '../registration/hooks/useRegistrationSource';
import {
    HANDLED_ERRORS_BY_CODES,
    OBJECT_DOES_NOT_EXIST_ERROR_CODE,
    STATIC_ERROR_PHRASES,
} from './constants';
import { authCheckEmailResolver } from './validation';

import partnersLogo from '../../images/auth/partners-mobile.png';

import css from './AuthCheckEmail.css';

const AuthCheckEmail: FC = () => {
    const [commonErrorMessage, setCommonErrorMessage] = useState<string>('');
    const [
        isActiveSessionExpiredPopup,
        setIsActiveSessionExpiredPopup,
    ] = useState(false);
    const history = useHistory();
    const searchQuery = getLocationQuery(history.location);
    const reasonLogout = searchQuery.reason_logout;

    const onCloseSessionExpiredPopup = () => {
        setIsActiveSessionExpiredPopup(false);
    };

    useEffect(() => {
        if (reasonLogout === 'session_expired') {
            setIsActiveSessionExpiredPopup(true);
        }
    }, [reasonLogout]);

    const isShowBackBtn = [...VALID_PROJECT_SOURCES, 'edo'].includes(
        (searchQuery?.source || '') as RegistrationProjectsSource,
    );

    const {
        control,
        handleSubmit,
        formState,
        setValue,
    } = useForm<AuthCheckEmailFormFields>({
        resolver: authCheckEmailResolver,
        defaultValues: {
            email: '',
        },
    });

    useEffect(() => {
        eventTracking.sendToGTM({
            event: 'funnel_reg_step_1',
            action: '',
            category: '',
        });

        const authInitialForm = getLocalStorageItem(LS_KEY_AUTH_INITIAL_FORM);

        if (authInitialForm?.email) {
            setValue('email', authInitialForm.email);
        }
    }, []);

    const sourceLink = useRegistrationSource();

    const isTTNAppContext = sourceLink === 'ttn';
    const isShowThirdPartyAuthMethods = !isTTNAppContext;

    const onSubmit: SubmitHandler<AuthCheckEmailFormFields> = async ({
        email,
    }) => {
        try {
            setCommonErrorMessage('');
            setLocalStorageItem(LS_KEY_AUTH_INITIAL_FORM, { email });
            await auth.checkUserIsRegistered(email);
            history.push({
                pathname: '/auth/login',
                search: stringifyLocationQuery(searchQuery),
            });
        } catch (error) {
            if (error.code === OBJECT_DOES_NOT_EXIST_ERROR_CODE) {
                eventTracking.sendToGTM({
                    event: 'funnel_reg_step_2_email',
                    action: 'success',
                    category: '',
                });
                history.push({
                    pathname: '/auth/registration',
                    search: stringifyLocationQuery(searchQuery),
                });
                return;
            }
            eventTracking.sendToGTM({
                event: 'funnel_reg_step_2_email',
                action: 'fail',
                category: '',
            });
            const errorMessage = HANDLED_ERRORS_BY_CODES.includes(error.code)
                ? error.reason
                : STATIC_ERROR_PHRASES.COMMON_ERROR;
            setCommonErrorMessage(errorMessage);
        }
    };

    const onBackHandler = () => {
        const url =
            LANDINGS_REDIRECT_LINKS_MAP[
                searchQuery?.source as keyof typeof LANDINGS_REDIRECT_LINKS_MAP
            ];

        if (url) {
            redirect(url);
        }
    };

    const handleAuthError = (error: { code: string; reason: string }) => {
        setCommonErrorMessage(error.reason);
    };

    /**
     * @see https://web.dev/articles/sign-in-form-best-practices for more details
     */
    return (
        <form
            id="login"
            style={{ height: '100%' }}
            onSubmit={handleSubmit(onSubmit)}
        >
            <PageTitle>{t`Вітаємо у Вчасно`}</PageTitle>
            <FlexBox
                className={css.container}
                align="center"
                justify="center"
                gap={0}
            >
                <div className={css.content}>
                    <FlexBox className={css.header} direction="column" gap={12}>
                        {isShowBackBtn && (
                            <BackButton
                                className={css.backBtn}
                                onClick={onBackHandler}
                            />
                        )}
                        <img
                            src={partnersLogo}
                            className={css.partnersLogos}
                            alt=""
                        />
                        <h1>
                            <span role="img" aria-label={t`Вітаємо`}>
                                👋
                            </span>{' '}
                            {t`Вітаємо у Вчасно`}
                        </h1>
                        <h4>{t`Авторизуйтеся у Вчасно за допомогою облікового запису або Email`}</h4>
                    </FlexBox>
                    <div>
                        {isShowThirdPartyAuthMethods && (
                            <>
                                <ThirdPartyAuthButtons
                                    microsoftAuthOn
                                    googleAuthOn
                                    appleAuthOn
                                    onError={handleAuthError}
                                />
                                <HorizontalLineSeparator
                                    className={css.horizontalLineSeparator}
                                >{t`або`}</HorizontalLineSeparator>
                            </>
                        )}
                        <Controller
                            control={control}
                            name="email"
                            render={({ field, fieldState }) => (
                                <OutlinedInput
                                    id="email"
                                    autoComplete="email"
                                    type="email"
                                    name="email"
                                    label={t`Email`}
                                    value={field.value}
                                    onChange={(event) => {
                                        field.onChange(
                                            event.target.value.trim(),
                                        );
                                    }}
                                    autoFocus
                                    maxLength={EMAIL_MAX_LENGTH + 1}
                                    error={fieldState.error?.message}
                                />
                            )}
                        />
                    </div>
                    {commonErrorMessage && (
                        <Alert theme="error" hideIcon>
                            {commonErrorMessage}
                        </Alert>
                    )}
                    <Button
                        className={css.submitBtn}
                        type="submit"
                        theme="darkGray"
                        isLoading={formState.isSubmitting}
                        disabled={formState.isSubmitting}
                    >{t`Продовжити`}</Button>
                </div>
            </FlexBox>
            <SessionExpiredPopup
                isActive={isActiveSessionExpiredPopup}
                onClose={onCloseSessionExpiredPopup}
            />
        </form>
    );
};

export default AuthCheckEmail;
